import { useEffect, useMemo, useState } from "react";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { TrendingUp, MapPin, Utensils, Calendar } from "lucide-react";
import { getRestaurants } from "@/lib/store";
import type { Restaurant } from "@/types";
import { parse } from "date-fns";
import { compositeScore20, scoreToneClass } from "@/lib/rating";

type TopCuisine = { name: string; count: number; percentage: number };

function safeParseVisitDate(s?: string): Date | undefined {
  if (!s) return undefined;
  const d = parse(s, "yyyy年M月d日", new Date());
  return isNaN(d as unknown as number) ? undefined : d;
}

export function InsightsDashboard() {
  const [list, setList] = useState<Restaurant[]>([]);

  useEffect(() => {
    setList(getRestaurants());
  }, []);

  const visited = useMemo(() => list.filter(r => !r.isWishlist), [list]);

  const averageRating = useMemo(() => {
    const vals = visited.map((r) => compositeScore20(r)).filter(v => v > 0);
    if (vals.length === 0) return 0;
    const avg = vals.reduce((a,b)=>a+b,0)/vals.length; // 0..20 平均
    return Math.round(avg*10)/10;
  }, [visited]);

  const totalVisited = visited.length;

  const monthlyVisits = useMemo(() => {
    const dates = visited.map(v => safeParseVisitDate(v.visitDate)).filter(Boolean) as Date[];
    if (dates.length === 0) return 0;
    const min = new Date(Math.min(...dates.map(d => d.getTime())));
    const max = new Date(Math.max(...dates.map(d => d.getTime())));
    const months = Math.max(1, (max.getFullYear()-min.getFullYear())*12 + (max.getMonth()-min.getMonth()) + 1);
    return Math.round((dates.length / months) * 10) / 10;
  }, [visited]);

  const topCuisines = useMemo<TopCuisine[]>(() => {
    if (visited.length === 0) return [];
    const map = new Map<string, number>();
    for (const r of visited) {
      const key = (r.cuisine || 'その他').trim();
      map.set(key, (map.get(key) || 0) + 1);
    }
    const total = [...map.values()].reduce((a,b)=>a+b,0) || 1;
    return [...map.entries()]
      .map(([name, count]) => ({ name, count, percentage: Math.round((count/total)*100) }))
      .sort((a,b)=> b.count - a.count)
      .slice(0,5);
  }, [visited]);

  const favoriteAreas = useMemo(() => {
    if (visited.length === 0) return [] as string[];
    const map = new Map<string, number>();
    for (const r of visited) {
      const area = (r.location || '未設定').trim();
      map.set(area, (map.get(area) || 0) + 1);
    }
    return [...map.entries()].sort((a,b)=>b[1]-a[1]).slice(0,5).map(([k])=>k);
  }, [visited]);

  const recentTrends = useMemo(() => {
    const trends: string[] = [];
    if (visited.length === 0) return ["まだ評価データがありません。まずは1件登録してみましょう。"];
    if (topCuisines[0]) trends.push(`いちばん多いジャンルは『${topCuisines[0].name}』です`);
    trends.push(`平均総合点は ${averageRating} です`);
    if (favoriteAreas[0]) trends.push(`よく行くエリアは『${favoriteAreas[0]}』です`);
    return trends;
  }, [visited, topCuisines, averageRating, favoriteAreas]);

  return (
    <div className="min-h-screen bg-background p-4">
      <div className="container mx-auto max-w-6xl space-y-8">
        {/* Header */}
        <div className="text-center space-y-2">
          <div className="flex items-center justify-center gap-2 mb-4">
            <TrendingUp className="h-8 w-8 text-primary" />
            <h1 className="text-3xl font-bold text-foreground">ふたりの食・インサイト</h1>
          </div>
          <p className="text-muted-foreground text-lg">ふたりの食の傾向をサクッと把握</p>
        </div>

        {/* Key Stats Row */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
          <Card className="p-6 text-center border-0 shadow-card bg-gradient-warm">
            <div className="text-2xl font-bold text-card-foreground mb-1">
              <span className={scoreToneClass(averageRating as number)}>{averageRating}</span>
            </div>
            <div className="text-sm text-muted-foreground">平均総合点（0–20）</div>
          </Card>

          <Card className="p-6 text-center border-0 shadow-card bg-gradient-warm">
            <MapPin className="h-8 w-8 text-primary mx-auto mb-3" />
            <div className="text-2xl font-bold text-card-foreground mb-1">{totalVisited}</div>
            <div className="text-sm text-muted-foreground">訪問店舗数</div>
          </Card>

          <Card className="p-6 text-center border-0 shadow-card bg-gradient-warm">
            <Calendar className="h-8 w-8 text-primary mx-auto mb-3" />
            <div className="text-2xl font-bold text-card-foreground mb-1">{monthlyVisits}</div>
            <div className="text-sm text-muted-foreground">月あたり訪問数</div>
          </Card>

          <Card className="p-6 text-center border-0 shadow-card bg-gradient-warm">
            <Utensils className="h-8 w-8 text-accent mx-auto mb-3" />
            <div className="text-2xl font-bold text-card-foreground mb-1">{topCuisines.length}</div>
            <div className="text-sm text-muted-foreground">好みジャンル数</div>
          </Card>
        </div>

        {/* Main Content Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Cuisine Preferences */}
          <Card className="p-6 border-0 shadow-warm">
            <div className="flex items-center gap-2 mb-6">
              <Utensils className="h-5 w-5 text-primary" />
              <h2 className="text-xl font-semibold text-card-foreground">好みの傾向</h2>
            </div>
            <div className="space-y-4">
              {topCuisines.map((cuisine, index) => (
                <div key={cuisine.name} className="space-y-2">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Badge variant="secondary" className="w-6 h-6 rounded-full p-0 flex items-center justify-center text-xs">
                        {index + 1}
                      </Badge>
                      <span className="font-medium text-card-foreground">{cuisine.name}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-sm text-muted-foreground">{cuisine.count} 件</span>
                      <span className="text-sm font-medium text-primary">{cuisine.percentage}%</span>
                    </div>
                  </div>
                  <div className="w-full bg-muted rounded-full h-2">
                    <div className="bg-gradient-primary h-2 rounded-full transition-all duration-500" style={{ width: `${cuisine.percentage}%` }} />
                  </div>
                </div>
              ))}
            </div>
          </Card>

          {/* Favorite Areas */}
          <Card className="p-6 border-0 shadow-warm">
            <div className="flex items-center gap-2 mb-6">
              <MapPin className="h-5 w-5 text-primary" />
              <h2 className="text-xl font-semibold text-card-foreground">よく行くエリア</h2>
            </div>
            <div className="space-y-3">
              {favoriteAreas.map((area, index) => (
                <div key={area} className="flex items-center justify-between p-3 rounded-lg bg-muted/30">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-full bg-gradient-primary flex items-center justify-center">
                      <span className="text-white text-sm font-medium">{index + 1}</span>
                    </div>
                    <span className="font-medium text-card-foreground">{area}</span>
                  </div>
                  <Badge variant="secondary">候補</Badge>
                </div>
              ))}
            </div>
          </Card>
        </div>

        {/* Recent Trends */}
        <Card className="p-6 border-0 shadow-warm">
          <div className="flex items-center gap-2 mb-6">
            <TrendingUp className="h-5 w-5 text-primary" />
            <h2 className="text-xl font-semibold text-card-foreground">最近の傾向</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {recentTrends.map((trend, index) => (
              <div key={index} className="p-4 rounded-lg bg-gradient-to-br from-primary/5 to-accent/5 border border-primary/10">
                <div className="flex items-start gap-3">
                  <div className="w-2 h-2 rounded-full bg-primary mt-2" />
                  <p className="text-sm text-card-foreground leading-relaxed">{trend}</p>
                </div>
              </div>
            ))}
          </div>
        </Card>
      </div>
    </div>
  );
}

